import React, { useMemo, useState, useEffect } from 'react';
import {
  Grid,
  TextField,
  InputAdornment,
  MenuItem,
  Button,
  Typography,
  Menu,
  styled,
  Select,
  IconButton,
} from '@mui/material';
import intl from 'components/i18n/ReactIntlWrapper';
import { useToastMessageQueue } from 'components/ToastMessages/ToastMessageProvider';
import { useInvoice } from '../common/InvoiceProvider';
import ClearIcon from '@mui/icons-material/Clear';
import { AppliesTo, SdsType } from 'common/constants';

const RedTextField = styled(TextField)(({ theme }) => ({
  '& .MuiOutlinedInput-root.Mui-error .MuiOutlinedInput-notchedOutline': {
    borderColor: 'red',
  },
  '& .MuiFormLabel-root.Mui-error': {
    color: 'red',
  },
}));

const NewInvoiceSummary = ({ disabled = false }) => {
  const [finalResult, setFinalResult] = useState(0);
  const { taxesValues, input, setInput, errors, setErrors } = useInvoice();
  const toast = useToastMessageQueue();
  const [sds, setSds] = useState({
    discount: 0,
    shipping: 0,
    otherCharges: 0,
  });
  const [sdsType, setSdsType] = useState({
    discountType: 1,
    shippingType: 1,
    otherChargesType: 1,
  });

  const [taxesAnchorEl, setTaxesAnchorEl] = useState<null | HTMLElement>(null);

  const taxesOpen = Boolean(taxesAnchorEl);

  const handleTaxesClick = (event: React.MouseEvent<HTMLElement>) => {
    setTaxesAnchorEl(event.currentTarget);
  };

  const handleTaxesClose = () => {
    setTaxesAnchorEl(null);
  };

  const selectTypes: { id: number; label: string }[] = [
    { id: 1, label: '$' },
    { id: 2, label: '%' },
  ];

  const subTotal = useMemo(() => {
    let total = 0;
    input?.invoiceItems?.forEach((item) => {
      total += Number(item.amount);
      if (item.taxesSum) {
        total += Number(item?.taxesSum);
      }
    });
    return total;
  }, [input?.invoiceItems]);

  const invoiceTaxes = taxesValues?.filter((tax) => tax.appliesTo === AppliesTo.Invoice);

  const subTotalWithoutTaxes = useMemo(() => {
    let total = 0;
    input?.invoiceItems?.forEach((item) => {
      total += Number(item.amount);
    });
    return total;
  }, [input?.invoiceItems]);

  useEffect(() => {
    setInput({
      ...input,
      shippingAmount: sdsType.shippingType === 1 ? sds.shipping : null,
      shippingPercentage: sdsType.shippingType === 2 ? sds.shipping : null,
      discountAmount: sdsType.discountType === 1 ? Math.abs(sds.discount) : null,
      discountPercentage: sdsType.discountType === 2 ? Math.abs(sds.discount) : null,
      otherChargesAmount: sdsType.otherChargesType === 1 ? sds.otherCharges : null,
      otherChargesPercentage: sdsType.otherChargesType === 2 ? sds.otherCharges : null,
    });
  }, [sdsType, sds]);

  const subTotalWithAddedValues = useMemo(() => {
    let total = 0;
    input?.invoiceItems?.forEach((item) => {
      total += Number(item.amount);
    });
    let addedValues = 0;
    if (sdsType.discountType === 1) {
      addedValues += sds.discount;
      const hasTaxes = input?.invoiceItems?.some(
        (item) => item.invoiceItemTaxes && item.invoiceItemTaxes.length > 0
      );
      if (hasTaxes) {
        setSdsType({ ...sdsType, discountType: 2 });
        if (sds.discount !== 0) {
          setSds({ ...sds, discount: 0 });
          toast.warning({
            header: `${intl.get('delete.modal.title')}`,
            body: `${intl.get('newInvoice.errorCantUse$')}`,
          });
        }
      }
    } else {
      addedValues += (total * sds.discount) / 100;
    }
    if (sdsType.otherChargesType === 1) {
      addedValues += sds.otherCharges;
    } else {
      addedValues += (total * sds.otherCharges) / 100;
    }
    if (sdsType.shippingType === 1) {
      addedValues += sds.shipping;
    } else {
      addedValues += (total * sds.shipping) / 100;
    }
    return total + addedValues;
  }, [input?.invoiceItems, sds, sdsType]);

  const handleNewTax = function (tax) {
    handleTaxesClose();
    if (input?.invoiceTaxes?.some((t) => t.idTaxRate === tax.id)) {
      return;
    }
    setInput({
      ...input,
      invoiceTaxes: [
        ...input.invoiceTaxes,
        {
          idTaxRate: tax.id,
          taxAmount: (tax.rate * subTotalWithoutTaxes) / 100,
          taxAmountValue: tax.amount,
          taxRate: tax.rate,
          description: tax.name,
          invoiceItem: {},
        },
      ],
    });
    handleTaxesClose();
  };

  const handleDeleteTax = function (idTaxRate, taxIndex) {
    const newItems = input?.invoiceTaxes?.filter((tax) => tax.idTaxRate !== idTaxRate);
    setInput({
      ...input,
      invoiceTaxes: [...newItems],
    });
    setErrors({
      ...errors,
      [`invoiceTaxes[${idTaxRate}].description`]: '',
    });
  };

  const validate = function (name, value, type) {
    if (value > 100 && type === 2) {
      setErrors({
        ...errors,
        [name]: `${intl.get('newInvoice.errorLowerThan100')}`,
      });
    } else if (value > subTotal && type === 1) {
      setErrors({
        ...errors,
        [name]: `${intl.get('newInvoice.errorLowerThanSubtotal')}`,
      });
    } else {
      if (errors?.[name] && errors?.[name] !== 'missing confidence') {
        const { [name]: omit, ...rest } = errors;
        setErrors(rest);
      }
    }
  };

  const getUsedTaxIds = () => {
    if (!input?.invoiceItems) return new Set();
    return new Set(
      input.invoiceItems.flatMap((item) => (item.invoiceItemTaxes || []).map((tax) => tax.id))
    );
  };

  useEffect(() => {
    validate(SdsType.DISCOUNT, Math.abs(sds.discount), sdsType.discountType);
  }, [sds.discount, sdsType.discountType, subTotal]);

  useEffect(() => {
    validate(SdsType.SHIPPING, Math.abs(sds.shipping), sdsType.shippingType);
  }, [sds.shipping, sdsType.shippingType, subTotal]);

  useEffect(() => {
    validate(SdsType.OTHER_CHARGES, Math.abs(sds.otherCharges), sdsType.otherChargesType);
  }, [sds.otherCharges, sdsType.otherChargesType, subTotal]);

  const handleSdsChange = function (e) {
    let subTotalWithChange = 0;
    if (e.target.name === SdsType.DISCOUNT) {
      subTotalWithChange = -Number(e.target.value);
    } else {
      subTotalWithChange = Number(e.target.value);
    }
    setSds({
      ...sds,
      [e.target.name]: Number(subTotalWithChange),
    });
    setErrors({
      ...errors,
      [e.target.name]: '',
    });
  };

  const handleSdsTypeChange = function (typeId, name) {
    const typeName = name + 'Type';
    setSdsType({ ...sdsType, [typeName]: typeId });
  };

  useEffect(() => {
    let itemsTaxesSum = 0;
    input?.invoiceItems?.forEach((item) => {
      if (item.taxesSum) {
        itemsTaxesSum += Number(item?.taxesSum);
      }
    });
    if (!input?.invoiceTaxes?.length) {
      setFinalResult(subTotalWithAddedValues + itemsTaxesSum);
    } else {
      let invoiceTaxesSum = 0;
      input?.invoiceTaxes?.forEach((tax) => {
        if (tax.taxRate > 0) {
          invoiceTaxesSum += (tax.taxRate * subTotalWithAddedValues) / 100;
        } else {
          invoiceTaxesSum += tax.taxAmountValue || 0;
        }
      });
      setFinalResult(subTotalWithAddedValues + invoiceTaxesSum + itemsTaxesSum);
    }
  }, [subTotalWithAddedValues, input?.invoiceTaxes, input?.invoiceItems]);

  const calculateTaxSummary = () => {
    if (!input?.invoiceItems || input?.invoiceItems?.length === 0) return [];
    // Group items by tax rate
    const taxGroups = input.invoiceItems.reduce((groups, item) => {
      // Skip if no taxes
      if (!item.invoiceItemTaxes || item.invoiceItemTaxes.length === 0) return groups;

      // Calculate base amount for this item
      const itemAmount = (item.quantity || 0) * (item.rate || 0);

      // Process each tax for the item
      item.invoiceItemTaxes.forEach((tax) => {
        const taxKey = `${tax.id}-${tax.rate}-${tax.name}`; // Unique key for each tax
        if (!groups[taxKey]) {
          groups[taxKey] = {
            id: tax.id,
            rate: tax.rate,
            name: tax.name,
            amount: tax.amount,
            totalTax: 0,
            baseAmount: 0,
          };
        }
        groups[taxKey].baseAmount += itemAmount;
        if (tax.rate > 0) {
          groups[taxKey].totalTax += itemAmount * (tax.rate / 100);
        } else {
          groups[taxKey].totalTax += Number(tax.amount || 0);
        }
      });
      return groups;
    }, {});

    return Object.values(taxGroups);
  };

  const getTaxLabel = (tax) => {
    if (tax.rate > 0) {
      return `${tax.name} [${tax.rate}%]`;
    }
    return `${tax.name} [$${tax.amount}]`;
  };

  const invoiceHasTaxes = input?.invoiceItems?.some(
    (item) => item.invoiceItemTaxes && item.invoiceItemTaxes.length > 0
  );

  return (
    <div className="container" style={{ zIndex: -1 }}>
      <div className="card-body">
        <Grid style={{ display: 'flex', alignItems: 'center' }}>
          <Grid
            className="justify-content-start"
            size={{
              md: 6,
            }}
          >
            {intl.get('newInvoice.subtotal')}
          </Grid>
          <Grid
            style={{ justifyContent: 'end', display: 'flex' }}
            size={{
              md: 6,
            }}
          >
            {subTotalWithoutTaxes.toFixed(2)}
          </Grid>
        </Grid>
        <Grid
          container
          style={{ marginTop: '15px', display: 'flex', alignItems: 'center' }}
          size={{
            xs: 12,
            md: 12,
            lg: 12,
            xl: 12,
          }}
        >
          <Grid
            size={{
              xs: 4,
              md: 4,
              lg: 4,
              xl: 4,
            }}
          >
            {intl.get('newInvoice.discount')}
          </Grid>
          <Grid
            size={{
              xs: 4,
              md: 4,
              lg: 4,
              xl: 4,
            }}
          >
            <RedTextField
              fullWidth
              type="number"
              name={SdsType.DISCOUNT}
              placeholder="0"
              onKeyDown={(e) => ['e', 'E', '+', '-'].includes(e.key) && e.preventDefault()}
              onChange={handleSdsChange}
              value={Math.abs(sds.discount)}
              size="small"
              onPaste={(e) => e.preventDefault()}
              error={!!errors?.discount}
              disabled={disabled}
              InputProps={{
                sx: { padding: 0 },
                endAdornment: (
                  <InputAdornment position="end">
                    <Select
                      sx={{ minWidth: 80 }}
                      value={sdsType.discountType}
                      onChange={(e) => handleSdsTypeChange(e.target.value, SdsType.DISCOUNT)}
                      disabled={invoiceHasTaxes || disabled}
                    >
                      {selectTypes?.map((type) => (
                        <MenuItem key={type.id} value={type.id}>
                          {`${type.label}`}
                        </MenuItem>
                      ))}
                    </Select>
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid
            style={{ justifyContent: 'end', display: 'flex' }}
            size={{
              xs: 4,
              md: 4,
              lg: 4,
              xl: 4,
            }}
          >
            {sdsType.discountType === 1
              ? sds.discount.toFixed(2)
              : Number((sds.discount * subTotalWithoutTaxes) / 100).toFixed(2)}
          </Grid>
          {errors?.discount && errors?.discount !== 'missing confidence' && (
            <Grid
              container
              style={{ justifyContent: 'center', marginTop: '5px', marginBottom: '-5px' }}
              size={{
                xs: 12,
                md: 12,
                lg: 12,
                xl: 12,
              }}
            >
              <Typography className="text-danger">{errors.discount}</Typography>
            </Grid>
          )}
        </Grid>
        <Grid
          container
          style={{ marginTop: '15px', display: 'flex', alignItems: 'center' }}
          size={{
            xs: 12,
            md: 12,
            lg: 12,
            xl: 12,
          }}
        >
          <Grid
            size={{
              xs: 4,
              md: 4,
              lg: 4,
              xl: 4,
            }}
          >
            {intl.get('newInvoice.shipping')}
          </Grid>
          <Grid
            size={{
              xs: 4,
              md: 4,
              lg: 4,
              xl: 4,
            }}
          >
            <RedTextField
              fullWidth
              type="number"
              name={SdsType.SHIPPING}
              placeholder="0"
              onKeyDown={(e) => ['e', 'E', '+', '-'].includes(e.key) && e.preventDefault()}
              onChange={handleSdsChange}
              size="small"
              onPaste={(e) => e.preventDefault()}
              error={!!errors?.shipping}
              disabled={disabled}
              InputProps={{
                sx: { padding: 0 },
                endAdornment: (
                  <InputAdornment position="end">
                    <Select
                      sx={{ minWidth: 80 }}
                      value={sdsType.shippingType}
                      onChange={(e) => handleSdsTypeChange(e.target.value, SdsType.SHIPPING)}
                      disabled={disabled}
                    >
                      {selectTypes?.map((type) => (
                        <MenuItem key={type.id} value={type.id}>
                          {`${type.label}`}
                        </MenuItem>
                      ))}
                    </Select>
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid
            style={{ justifyContent: 'end', display: 'flex' }}
            size={{
              xs: 4,
              md: 4,
              lg: 4,
              xl: 4,
            }}
          >
            {sdsType.shippingType === 1
              ? sds.shipping.toFixed(2)
              : Number((sds.shipping * subTotalWithoutTaxes) / 100).toFixed(2)}
          </Grid>
          {errors?.shipping && errors?.shipping !== 'missing confidence' && (
            <Grid
              container
              style={{ justifyContent: 'center', marginTop: '5px', marginBottom: '-5px' }}
              size={{
                xs: 12,
                md: 12,
                lg: 12,
                xl: 12,
              }}
            >
              <Typography className="text-danger">{errors.shipping}</Typography>
            </Grid>
          )}
        </Grid>

        <Grid
          container
          style={{ marginTop: '15px', display: 'flex', alignItems: 'center' }}
          size={{
            xs: 12,
            md: 12,
            lg: 12,
            xl: 12,
          }}
        >
          <Grid
            size={{
              xs: 4,
              md: 4,
              lg: 4,
              xl: 4,
            }}
          >
            {intl.get('newInvoice.other.charges')}
          </Grid>
          <Grid
            size={{
              xs: 4,
              md: 4,
              lg: 4,
              xl: 4,
            }}
          >
            <RedTextField
              fullWidth
              type="number"
              name={SdsType.OTHER_CHARGES}
              placeholder="0"
              onKeyDown={(e) => ['e', 'E', '+', '-'].includes(e.key) && e.preventDefault()}
              onChange={handleSdsChange}
              size="small"
              onPaste={(e) => e.preventDefault()}
              error={!!errors?.otherCharges}
              disabled={disabled}
              InputProps={{
                sx: { padding: 0 },
                endAdornment: (
                  <InputAdornment position="end">
                    <Select
                      sx={{ minWidth: 80 }}
                      value={sdsType.otherChargesType}
                      onChange={(e) => handleSdsTypeChange(e.target.value, SdsType.OTHER_CHARGES)}
                      disabled={disabled}
                    >
                      {selectTypes?.map((type) => (
                        <MenuItem key={type.id} value={type.id}>
                          {`${type.label}`}
                        </MenuItem>
                      ))}
                    </Select>
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid
            style={{ justifyContent: 'end', display: 'flex' }}
            size={{
              xs: 4,
              md: 4,
              lg: 4,
              xl: 4,
            }}
          >
            {sdsType.otherChargesType === 1
              ? sds.otherCharges.toFixed(2)
              : Number((sds.otherCharges * subTotalWithoutTaxes) / 100).toFixed(2)}
          </Grid>
          {errors?.otherCharges && errors?.otherCharges !== 'missing confidence' && (
            <Grid
              container
              style={{ justifyContent: 'center', marginTop: '5px', marginBottom: '-5px' }}
              size={{
                xs: 12,
                md: 12,
                lg: 12,
                xl: 12,
              }}
            >
              <Typography className="text-danger">{errors.otherCharges}</Typography>
            </Grid>
          )}
        </Grid>
        <hr />
        <div className="mt-3">
          {calculateTaxSummary()?.map((taxGroup: any) => (
            <Grid
              key={`${taxGroup.id}-${taxGroup.rate}`}
              style={{
                display: taxGroup.name ? 'flex' : 'none',
                alignItems: 'center',
                marginBottom: '5px',
              }}
            >
              <Grid
                className="justify-content-start"
                size={{
                  md: 6,
                }}
              >
                {`${taxGroup.name} [${taxGroup.rate > 0 ? `${taxGroup.rate}%` : `$${taxGroup.amount}`}]`}
              </Grid>
              <Grid
                style={{ display: 'flex', justifyContent: 'middle' }}
                size={{
                  md: 3,
                }}
              >
                {Number(taxGroup.baseAmount).toFixed(2)}
              </Grid>
              <Grid
                style={{ display: 'flex', justifyContent: 'flex-end' }}
                size={{
                  md: 3,
                }}
              >
                {Number(taxGroup.totalTax).toFixed(2)}
              </Grid>
            </Grid>
          ))}
        </div>
        {input?.invoiceTaxes?.map((tax, index) => {
          return (
            <Grid
              container
              key={tax.idTaxRate}
              style={{ display: 'flex', alignItems: 'center' }}
              size={{
                md: 12,
              }}
            >
              <Grid
                container
                className="justify-content-start"
                size={{
                  md: 6,
                }}
              >
                <Grid
                  size={{
                    md: 7,
                  }}
                >
                  <a
                    style={{
                      color:
                        errors?.[`invoiceTaxes[${tax.idTaxRate}].description`] ===
                        'missing confidence'
                          ? 'red'
                          : 'inherit',
                    }}
                  >
                    {tax['description']} [{tax['taxRate'] === 0 ? '$' : ''}
                    {tax['taxRate'] > 0 ? tax['taxRate'] : tax['taxAmountValue']}
                    {tax['taxRate'] > 0 ? '%' : ''}]
                  </a>
                </Grid>
                <Grid
                  style={{ justifyContent: 'flex-start' }}
                  size={{
                    md: 5,
                  }}
                >
                  <IconButton
                    size="small"
                    onClick={() => handleDeleteTax(tax.idTaxRate, index + 1)}
                    style={{ cursor: 'pointer' }}
                    aria-label="delete"
                    color="error"
                    disabled={disabled}
                  >
                    <ClearIcon width="24" height="24" fontSize="inherit" />
                  </IconButton>
                </Grid>
              </Grid>
              <Grid
                style={{ justifyContent: 'end', display: 'flex' }}
                size={{
                  md: 6,
                }}
              >
                {tax['taxRate'] > 0
                  ? Number((tax.taxRate * subTotalWithAddedValues) / 100).toFixed(2)
                  : Number(tax?.taxAmountValue || 0).toFixed(2)}
              </Grid>
            </Grid>
          );
        })}
        <div className="d-flex justify-content-end my-1">
          <Button
            variant="text"
            style={{
              color: disabled ? 'gray' : 'black',
              marginTop: '10px',
              cursor: 'pointer',
              textDecoration: 'underline',
            }}
            onClick={handleTaxesClick}
            disabled={disabled}
          >
            +{intl.get('newInvoice.add.taxes')}
          </Button>
          <Menu
            elevation={0}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'right',
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'right',
            }}
            anchorEl={taxesAnchorEl}
            open={taxesOpen}
            onClose={handleTaxesClose}
          >
            {invoiceTaxes?.length > 0 ? (
              invoiceTaxes
                .filter((tax) => !getUsedTaxIds().has(tax.id))
                .map((tax) => (
                  <MenuItem key={tax.id} onClick={() => handleNewTax(tax)} disabled={disabled}>
                    {getTaxLabel(tax)}
                  </MenuItem>
                ))
            ) : (
              <MenuItem disabled>{intl.get('placeholder.noOptions')}</MenuItem>
            )}
          </Menu>
        </div>
        <Grid
          style={{
            marginTop: '15px',
            display: 'flex',
            background: '#E7F4FF',
            padding: '5px',
            borderRadius: '5px',
          }}
        >
          <Grid
            className="justify-content-start"
            size={{
              md: 6,
            }}
          >
            <b>{intl.get('newInvoice.total')}</b>
          </Grid>
          <Grid
            style={{ justifyContent: 'end', display: 'flex' }}
            size={{
              md: 6,
            }}
          >
            <b>{Number(finalResult).toFixed(2)}</b>
          </Grid>
        </Grid>
      </div>
    </div>
  );
};

export default NewInvoiceSummary;
